import {Alert, Button, Divider, Form, Input, Menu, Row, Select, Slider, Switch, Tooltip} from 'antd';
import {CopyOutlined, EyeOutlined, InfoCircleOutlined} from '@ant-design/icons';
import {ErrorMessage, Formik} from 'formik';
import moment from 'moment-timezone';
import React from 'react';
import {isMobile} from 'react-device-detect';

import {Client, Country, User} from '../../../../types/src';
import {brandConfig} from '../../utils';
import {AdminFeatureIcon, ConfirmationModal, FormRow, InfoIcon, LockIcon} from '../';
import {checkCloudOffline, CONSTANTS, STRINGS} from '../..';
import {SwitchRow} from '../campaignDetails/common';
import {BaseModal} from '../BaseModal';
import {ProxySettings} from '../ProxySettings';
import {
  getUserCampaignScheduleInitialValues,
  getUserCampaignScheduleValidationError,
  shouldShowUserCampaignScheduleFields,
  UserCampaignScheduleFields,
} from './campaignScheduleFields';
import {UserRequirementsPopover} from './userRequirementsPopover';
import {UserRequirementsTooltip} from './userRequirementsTooltip';
import {TermsAndConditions} from './TermsAndConditions';
import {UserActionDropdown} from './UserActionDropdown';

import './styles.scss';

interface UserEditModalProps {
  user: User;
  users: User[];
  client: Client;
  members: any;
  visible: boolean;
  isAdmin: boolean;
  loading: boolean;
  error: string;
  isNewUser: boolean;
  affiliatedClients: any;
  serverState?: string;
  handleOk: (user: User) => Promise<User>;
  handleCancel: () => void;
  cleanup: () => void;
  archiveUser: (userId: number) => void;
  unarchiveUser: (userId: number) => void;
  scheduleArchive: (userId: number) => void;
  cancelScheduledArchive: (userId: number) => void;
  revealPassword: (userId: number) => void;
  changeUserState: (userId: number, value: boolean) => void;
  snoozeLoginError: (userId: number, days: number) => void;
  replaceUser: (userId: number) => void;
  validateUser: (userId: number) => void;
  openUser: (userId: number) => void;
  handleTwitter: (userId: number, state: boolean) => void;
  trackIntercomEvent: (tag: any, value: any, condition?: any) => void;
  updateSnSubsctiptionSelect: (userId: number, value: number) => void;
  clearCookies: (userId: number) => void;
  resetLoginErrorDate: (userId: number) => void;
  getAffiliatedClients: () => void;
  moveUser: (params: any, withCampaigns: boolean) => void;
  removeProxy: (id: number) => void;
  getProxyLocations: () => Promise<Country[]>;
  locationsInfo: any;
  suggestedProxy: any;
}

interface UserEditModalState {
  login_error_snooze_days: number;
  selectedClient: any;
  moveUserWithCampaigns: boolean;
  isCustomProxy: boolean;
  confirmationVisible: boolean;
  isDirty: boolean;
}

const snSubsctiptionOptions = [
  {label: 'First', value: 1},
  {label: 'Second', value: 2},
  {label: 'Third', value: 3},
];

export class UserEditModal extends React.Component<UserEditModalProps, UserEditModalState> {
  state = {
    login_error_snooze_days: 1,
    selectedClient: null,
    moveUserWithCampaigns: true,
    isCustomProxy: false,
    confirmationVisible: false,
    isDirty: false,
  };

  componentDidMount() {
    if (this.props.isAdmin) {
      this.props.getAffiliatedClients();
    }
  }

  setIsCustomProxy = (value: boolean) => {
    this.setState({isCustomProxy: value});
  };

  getSubmitTextButton = (currentPassword): string => {
    if (this.props.isNewUser || currentPassword) {
      return 'Save and Validate';
    }

    return 'Save';
  };

  handleValidate = (values: any) => {
    const errors: any = {};
    if (!values.first_name) {
      errors.first_name = 'Required';
    }
    if (!values.last_name) {
      errors.last_name = 'Required';
    }
    if (!values.email) {
      errors.email = 'Required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = 'Must be an email';
    }
    if (!this.props.user.id && !values.password) {
      errors.password = 'Required';
    }
    if (values.weekly_max_messages_limit > 200) {
      errors.weekly_max_messages_limit = 'Weekly max messages limit is 200';
    }
    const invites_restricted_max = this.props.user.invites_restricted_max || Number.MAX_SAFE_INTEGER;
    if (!values.max_messages_limit) {
      errors.max_messages_limit = 'Required';
    } else if (values.max_messages_limit > Math.min(this.props.user.max_messages, invites_restricted_max)) {
      errors.max_messages_limit =
        this.props.user.max_messages <= invites_restricted_max
          ? `The max daily invites limit is ${this.props.user.max_messages}`
          : `The max daily invites limit is temporary restricted to ${this.props.user.invites_restricted_max}`;
    }
    if (this.props.client.premium_feature && !values.max_nurturing_message_limit) {
      errors.max_nurturing_message_limit = 'Required';
    }
    if (values.exclude_companies && values.exclude_companies.length > 70000) {
      errors.exclude_companies = STRINGS.error_max_exclude_companies_length;
    }
    const campaignsStartTimeError = getUserCampaignScheduleValidationError(values.campaigns_start_time);
    if (campaignsStartTimeError) {
      errors.campaigns_start_time = campaignsStartTimeError;
    }
    if (this.state.isCustomProxy) {
      if (!values.custom_proxy_ip) {
        errors.custom_proxy_ip = 'Required';
      }
      if (!values.custom_proxy_password) {
        errors.custom_proxy_password = 'Required';
      }
      if (!values.custom_proxy_port) {
        errors.custom_proxy_port = 'Required';
      }
      if (!values.custom_proxy_username) {
        errors.custom_proxy_username = 'Required';
      }
    }

    return errors;
  };

  isClientOffline = () => {
    return !this.props.client.k8s_support && checkCloudOffline(this.props.client.last_activity_date, this.props.serverState);
  }

  handleSubmit = async (values: any) => {
    this.props.handleOk(values);

    if (this.state.selectedClient) {
      this.props.moveUser(
        {user_id: this.props.user.id, new_client_id: this.state.selectedClient},
        this.state.moveUserWithCampaigns
      );
    }

    const sn_account_selection = values.sn_account_selection;
    if (sn_account_selection !== this.props.user.sn_account_selection) {
      this.props.updateSnSubsctiptionSelect(this.props.user.id, values.sn_account_selection);
    }
  };

  handleAction = async ({key}: any) => {
    if (key === 'archive') {
      this.props.archiveUser(this.props.user.id);
    } else if (key === 'replace') {
      this.props.replaceUser(this.props.user.id);
    } else if (key === 'validate') {
      this.props.validateUser(this.props.user.id);
    } else if (key === 'unarchive') {
      this.props.unarchiveUser(this.props.user.id);
    } else if (key === 'open') {
      this.props.openUser(this.props.user.id);
    } else if (key === 'cancel-scheduled-archive') {
      this.props.cancelScheduledArchive(this.props.user.id);
    } else if (key === 'schedule-archive') {
      this.props.scheduleArchive(this.props.user.id);
    }
  };

  onClose = () => {
    if (this.state.isDirty) {
      this.setState({confirmationVisible: true});
    } else {
      this.closeModal();
    }
  };

  closeModal = () => {
    this.setState({confirmationVisible: false});
    this.props.cleanup();
    this.props.handleCancel();
  };

  revealPassword = () => {
    this.props.revealPassword(this.props.user.id);
  };

  snoozeLoginError = () => {
    this.props.snoozeLoginError(this.props.user.id, this.state.login_error_snooze_days);
  };

  getMenu = () => {
    if (this.props.user.archived && this.props.isAdmin) {
      return (
        <UserActionDropdown onClick={this.handleAction}>
          <Menu.Item key="unarchive">
            <AdminFeatureIcon />
            Unarchive
          </Menu.Item>
        </UserActionDropdown>
      );
    }
    if (this.props.user.id && this.props.client.affiliate) {
      return (
        <UserActionDropdown onClick={this.handleAction}>
          <Menu.Item key="validate" disabled={this.isClientOffline()}>
            Validate
          </Menu.Item>
          {this.props.client.open_feature && (
            <Menu.Item key="open" disabled={this.isClientOffline()}>
              Open
            </Menu.Item>
          )}
          <Menu.Item key="replace">
            Replace <InfoIcon message={STRINGS.tooltip_replace_user} />
          </Menu.Item>
          <Menu.Item key="archive">
            Archive <InfoIcon message={STRINGS.tooltip_archive_user} />
          </Menu.Item>
          {this.props.isAdmin &&
            (this.props.user.scheduled_archive_date ? (
              <Menu.Item key="cancel-scheduled-archive">
                <AdminFeatureIcon />
                Cancel Scheduled Archivation
              </Menu.Item>
            ) : (
              <Menu.Item key="schedule-archive">
                <AdminFeatureIcon />
                Schedule Archive
              </Menu.Item>
            ))}
        </UserActionDropdown>
      );
    }

    return (
      <UserActionDropdown onClick={this.handleAction}>
        <Menu.Item key="validate" disabled={this.isClientOffline()}>
          Validate
        </Menu.Item>
        {this.props.client.open_feature && (
          <Menu.Item key="open" disabled={this.isClientOffline()}>
            Open
          </Menu.Item>
        )}
        <Menu.Item key="archive">
          <span>
            {this.props.isAdmin && <AdminFeatureIcon />}
            Archive <InfoIcon message={STRINGS.tooltip_archive_user} />
          </span>
        </Menu.Item>
        {this.props.isAdmin &&
          (this.props.user.scheduled_archive_date ? (
            <Menu.Item key="cancel-scheduled-archive">
              <AdminFeatureIcon />
              Cancel Scheduled Archivation
            </Menu.Item>
          ) : (
            <Menu.Item key="schedule-archive">
              <AdminFeatureIcon />
              Schedule Archive
            </Menu.Item>
          ))}
      </UserActionDropdown>
    );
  };

  isLockCreateTeamMember = email => {
    const {users, client, members} = this.props;
    return client.email === email || users.some(u => u.email === email) || members.some(m => m.email === email);
  };

  handleChangeEmail = (value, setFieldValue) => {
    setFieldValue('email', value);
    setFieldValue('create_team_member', !this.isLockCreateTeamMember(value));
  };

  render() {
    const {isAdmin, affiliatedClients} = this.props;
    const isUpdateUser = !!this.props.user.id;
    const isFirstUserForClient = this.props.users.length === 0;
    const twitter_connected =
      !!this.props.user.twitter_creds &&
      !!this.props.user.twitter_creds.access_oauth_token_secret &&
      !!this.props.user.twitter_creds.access_oauth_token;

    return (
      <BaseModal
        visible={this.props.visible || this.props.loading}
        onClose={this.onClose}
        loading={this.props.loading}
        title={
          <div className="userEditModalTitle">
            <span>User Details</span> <span>{this.getMenu()}</span>
          </div>
        }
        className="userEditModal"
      >
        <Formik
          initialValues={{
            first_name: this.props.user.first_name,
            last_name: this.props.user.last_name,
            email: this.props.user.email,
            password: this.props.user.password,
            max_messages_limit: this.props.user.max_messages_limit,
            max_nurturing_message_limit: this.props.user.max_nurturing_message_limit,
            delete_pending_invites: this.props.user.delete_pending_invites,
            auto_update_messages_limits: this.props.user.auto_update_messages_limits,
            weekly_max_messages_limit: this.props.user.weekly_max_messages_limit,
            weekly_invites_limit_via_email: this.props.user.weekly_invites_limit_via_email,
            cookies: this.props.user.cookies,
            user_id: this.props.user.id,
            id: this.props.user.id,
            last_login_error_date: this.props.user.last_login_error_date,
            ignore_actions_with_receivers_from_user: this.props.user.ignore_actions_with_receivers_from_user,
            sn_account_selection: this.props.user.sn_account_selection,
            disabled: this.props.user.disabled,
            create_team_member: this.props.user.create_team_member,
            basic_linkedin_sub: this.props.user.basic_linkedin_sub,
            exclude_companies: this.props.user.exclude_companies || '',
            ...getUserCampaignScheduleInitialValues(this.props.user, this.props.client),
          }}
          enableReinitialize={true}
          validate={this.handleValidate}
          onSubmit={this.handleSubmit}
          innerRef={(ref) => {
            if (ref && ref.dirty !== this.state.isDirty) {
              this.setState({isDirty: ref.dirty})
            }
          }}
        >
          {({values, errors, touched, dirty, handleChange, handleBlur, handleSubmit, setFieldValue}) => (
            <Form onFinish={handleSubmit} autoComplete="off" layout="vertical">
              <div className="userEditModalFormContainer">
                {!!this.props.user.archived && (
                  <div className="viewOnlyOverlay">
                    <span className="viewOnlyOverlayText">
                      <EyeOutlined /> View only
                    </span>
                  </div>
                )}
                <FormRow>
                  <Form.Item label="First Name*:">
                    <Input
                      value={values.first_name}
                      name="first_name"
                      className={errors.first_name && touched.first_name ? 'invalid-input' : ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Type your user first name as it appears on LinkedIn"
                    />
                    <ErrorMessage name="first_name" className="error-message" component="div" />
                  </Form.Item>
                </FormRow>
                <FormRow>
                  <Form.Item label="Last Name*:">
                    <Input
                      value={values.last_name}
                      name="last_name"
                      className={errors.last_name && touched.last_name ? 'invalid-input' : ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Type your user last name as it appears on LinkedIn"
                    />
                    <ErrorMessage name="last_name" className="error-message" component="div" />
                  </Form.Item>
                </FormRow>
                {this.props.user.linkedin_url && (
                  <FormRow>
                    <Form.Item label="Linkedin Profile:">
                      <a href={this.props.user.linkedin_url} target="_blank" rel="noreferrer">
                        {this.props.user.linkedin_url}
                      </a>
                    </Form.Item>
                  </FormRow>
                )}
                <FormRow>
                  <Form.Item
                    label={
                      <span>
                        <span>Email:</span>{' '}
                        <Tooltip title="Please enter your correct LinkedIn login email and password.">
                          <InfoCircleOutlined style={{marginRight: 5}} />
                        </Tooltip>
                        {isAdmin && <AdminFeatureIcon tooltip="This feature is editable only by admin" />}
                      </span>
                    }
                  >
                    <Input
                      value={values.email}
                      name="email"
                      className={errors.email && touched.email ? 'invalid-input' : ''}
                      disabled={isUpdateUser && !isAdmin}
                      autoComplete="client-user-email"
                      onChange={e => this.handleChangeEmail(e.target.value, setFieldValue)}
                      onBlur={handleBlur}
                      placeholder="Type your user linkedin email used for logging in on LinkedIn"
                    />
                    <ErrorMessage name="email" className="error-message" component="div" />
                  </Form.Item>
                </FormRow>
                {shouldShowUserCampaignScheduleFields(this.props.client, this.props.user) && (
                  <FormRow>
                    <UserCampaignScheduleFields
                      timezone={values.timezone}
                      campaigns_start_time={values.campaigns_start_time}
                      error={errors.campaigns_start_time}
                      touched={touched.campaigns_start_time}
                      onTimezoneChange={value => setFieldValue('timezone', value)}
                      onStartTimeChange={value =>
                        setFieldValue('campaigns_start_time', value ? value.format(CONSTANTS.main_date_format) : null)
                      }
                    />
                  </FormRow>
                )}
                {isAdmin && (
                  <FormRow>
                    <Form.Item label={<span>Password*: {isAdmin && <AdminFeatureIcon />}</span>}>
                      <Input type="password" style={{display: 'none'}} />{' '}
                      {/* 'hack for preventing browser save password popup' */}
                      <Input
                        type="password"
                        autoComplete="new-password"
                        value={values.password}
                        name="password"
                        className={errors.password && touched.password ? 'invalid-input' : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={
                          this.props.user.id
                            ? '*******************'
                            : 'Type your user LinkedIn password used for logging in'
                        }
                        addonAfter={
                          isAdmin ? (
                            <CopyOutlined style={{cursor: 'pointer'}} onClick={this.revealPassword} type="copy" />
                          ) : null
                        }
                      />
                      <ErrorMessage name="password" className="error-message" component="div" />
                    </Form.Item>
                  </FormRow>
                )}

                {this.props.client.proxy_feature && (
                  <ProxySettings
                    client={this.props.client}
                    proxy={this.props.user.proxy}
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    isAdmin={isAdmin}
                    isCustomProxy={this.state.isCustomProxy}
                    setIsCustomProxy={this.setIsCustomProxy}
                    removeProxy={this.props.removeProxy}
                    getProxyLocations={this.props.getProxyLocations}
                    locationsInfo={this.props.locationsInfo}
                    suggestedProxy={this.props.suggestedProxy}
                  />
                )}
                <FormRow>
                  <Form.Item
                    className="userEditModalSliderFormItem"
                    labelCol={{span: 13}}
                    wrapperCol={{span: 11}}
                    label={
                      <span>
                        <span>Daily Invites Limit (acquisition):</span>{' '}
                        <Tooltip
                          title={
                            <span>
                              Be aware that the daily invites limit only applies to normal acquisition campaigns. For
                              more information about limits for Acquisition+,{' '}
                              {(brandConfig.isGx || brandConfig.isLeadoku) && (
                                <a
                                  style={{textDecoration: 'underline'}}
                                  rel="noreferrer"
                                  href={brandConfig.articles.acquisitionCampaignEmail}
                                  target="_blank"
                                >
                                  click here.
                                </a>
                              )}
                            </span>
                          }
                        >
                          <InfoCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </span>
                    }
                  >
                    <div className="sliderWrapper">
                      <span>0</span>
                      <Slider
                        min={0}
                        max={this.props.user.max_messages}
                        step={1}
                        defaultValue={50}
                        value={values.max_messages_limit}
                        onChange={val => setFieldValue('max_messages_limit', val)}
                      />
                      <span>{this.props.user.max_messages}</span>
                    </div>
                    <ErrorMessage name="max_messages_limit" className="error-message" component="div" />
                  </Form.Item>
                </FormRow>
                <FormRow>
                  <Form.Item
                    className="userEditModalSliderFormItem"
                    labelCol={{span: 13}}
                    wrapperCol={{span: 11}}
                    label={
                      <span>
                        <span>Weekly Invites Limit (acquisition):</span>{' '}
                        <Tooltip
                          title="The maximum number of weekly invites allowed by LinkedIn is 200. If you are sending any
                          invites manually during the week with this user, make sure to reduce this number. For example
                          if you send about 10 invites manually every week, makes sure to reduce this number to 190."
                        >
                          <InfoCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </span>
                    }
                  >
                    <div className="sliderWrapper">
                      <span>0</span>
                      <Slider
                        min={0}
                        max={this.props.client.weekly_invites_limit || 200}
                        step={1}
                        defaultValue={180}
                        value={values.weekly_max_messages_limit}
                        onChange={val => setFieldValue('weekly_max_messages_limit', val)}
                      />
                      <span>{this.props.client.weekly_invites_limit || 200}</span>
                    </div>
                  </Form.Item>
                </FormRow>

                {this.props.client.import_emails_feature && (
                  <FormRow>
                    <Form.Item
                      className="userEditModalSliderFormItem"
                      labelCol={{span: 13}}
                      wrapperCol={{span: 11}}
                      label={
                        <span>
                          <span>Weekly Invites Limit (acq+):</span>{' '}
                          <Tooltip title="This value defines the maximum number of weekly invites sent with Acquisition+ campaigns only.">
                            <InfoCircleOutlined className="tooltip-icon" />
                          </Tooltip>
                        </span>
                      }
                    >
                      <div className="sliderWrapper">
                        <span>0</span>
                        <Slider
                          min={0}
                          max={this.props.user.weekly_invites_via_email_upper_limit}
                          step={1}
                          defaultValue={50}
                          value={values.weekly_invites_limit_via_email}
                          onChange={val => setFieldValue('weekly_invites_limit_via_email', val)}
                        />
                        <span>{this.props.user.weekly_invites_via_email_upper_limit}</span>
                      </div>
                    </Form.Item>
                  </FormRow>
                )}

                <FormRow>
                  <Form.Item
                    className="userEditModalSliderFormItem"
                    labelCol={{span: 13}}
                    wrapperCol={{span: 11}}
                    label={
                      <span>
                          <span>Daily Messages Limit (nurturing):</span>{' '}
                        <Tooltip
                          title="This limit defines the maximum number of first nurturing messages sent by nurturing
                          campaigns. This limit doesn't take in consideration the followups sent by nurturing
                          campaigns."
                        >
                            <InfoCircleOutlined className="tooltip-icon" />
                          </Tooltip>
                        </span>
                    }
                  >
                    <div className="sliderWrapper">
                      <span>0</span>
                      <Slider
                        min={0}
                        max={100}
                        step={1}
                        defaultValue={100}
                        value={values.max_nurturing_message_limit}
                        onChange={val => setFieldValue('max_nurturing_message_limit', val)}
                      />
                      <span>100</span>
                    </div>
                  </Form.Item>
                </FormRow>
                <FormRow>
                  <Form.Item
                    layout="horizontal"
                    label={
                      <span>
                        <span>Increase daily invites limit automatically:</span>{' '}
                        <Tooltip title="Daily invite limit will automatically increase gradually following our own internal rules to keep your account safe.">
                          <InfoCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </span>
                    }
                  >
                    <div className="userEditModalFormRow">
                      {!isUpdateUser ? (
                        <div className="userEditModalFormRowWithLock">
                          <LockIcon message={STRINGS.info_user_pending_invites_lock} />
                        </div>
                      ) : null}
                      <Switch
                        style={{float: 'right'}}
                        disabled={!isUpdateUser}
                        onChange={val => setFieldValue('auto_update_messages_limits', val)}
                        checked={!!values.auto_update_messages_limits}
                      />
                    </div>
                  </Form.Item>
                </FormRow>
                <FormRow>
                  <Form.Item
                    layout="horizontal"
                    label={
                      <span>
                        <span>Delete Pending Invites:</span>{' '}
                        <Tooltip title="By checking this, any pending invite older than 30 days will be deleted, this is recommended to keep the user in good standing.">
                          <InfoCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </span>
                    }
                  >
                    <div className="userEditModalFormRow">
                      <div className="userEditModalFormRowWithLock">
                        <LockIcon message={STRINGS.info_user_pending_invites_lock} />
                      </div>
                      <Switch
                        style={{float: 'right'}}
                        disabled={true}
                        onChange={val => setFieldValue('delete_pending_invites', val)}
                        checked={!!values.delete_pending_invites}
                      />
                    </div>
                  </Form.Item>
                </FormRow>
                {isAdmin && (
                  <FormRow>
                    <Form.Item
                      layout="horizontal"
                      label={
                        <span>
                          <span>Use only Basic LI:</span>{' '}
                          <Tooltip title="By checking this, user will use only Basic LI.">
                            <InfoCircleOutlined className="tooltip-icon" />
                          </Tooltip>
                        </span>
                      }
                    >
                      <div className="userEditModalFormRow">
                        <Switch
                          style={{float: 'right'}}
                          onChange={val => setFieldValue('basic_linkedin_sub', val)}
                          checked={!!values.basic_linkedin_sub}
                        />
                      </div>
                    </Form.Item>
                  </FormRow>
                )}
                <FormRow>
                  <Form.Item
                    layout="horizontal"
                    label={
                      <span>
                        <span>Twitter Integration:</span>{' '}
                        <Tooltip title={STRINGS.tooltip.twitter_connect}>
                          <InfoCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </span>
                    }
                  >
                    <div className="userEditModalFormRow">
                      {!this.props.client.twitter_feature && (
                        <div className="userEditModalFormRowWithLock">
                          <LockIcon
                            onClick={() =>
                              this.props.trackIntercomEvent(
                                'twitter_feature_request',
                                true,
                                !this.props.client.twitter_feature
                              )
                            }
                            message={STRINGS.message_unlock_feature}
                          />
                        </div>
                      )}
                      <Button
                        size="small"
                        disabled={!this.props.client.twitter_feature}
                        type={twitter_connected ? 'default' : 'primary'}
                        onClick={() => {
                          this.props.handleTwitter(this.props.user.id, !twitter_connected);
                        }}
                      >
                        {twitter_connected ? 'Disconnect' : 'Connect'}
                      </Button>
                    </div>
                  </Form.Item>
                </FormRow>
                <FormRow>
                  <SwitchRow
                    label={
                      <span>
                        Allow other users to contact this user's connections and prospects:{' '}
                        <InfoIcon
                          // filled={false}
                          className="tooltip-icon"
                          message={STRINGS.tooltip.exclude_user}
                        />
                      </span>
                    }
                    onChange={(checked: boolean) => setFieldValue('ignore_actions_with_receivers_from_user', checked)}
                    checked={!!values.ignore_actions_with_receivers_from_user}
                    isLock={false}
                  />
                </FormRow>
                <FormRow>
                  <Form.Item
                    label={
                      <span>
                        <span>Exclude Companies:</span>{' '}
                        <InfoIcon className="tooltip-icon" message={STRINGS.tooltip.blacklist_companies_user} />
                      </span>
                    }
                  >
                    <Input.TextArea
                      value={values.exclude_companies}
                      name="exclude_companies"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={STRINGS.placeholder.blacklist_companies}
                      rows={3}
                    />
                    <ErrorMessage name="exclude_companies" className="error-message" component="div" />
                  </Form.Item>
                </FormRow>
                {this.props.isNewUser && (
                  <FormRow>
                    <SwitchRow
                      checked={values.create_team_member}
                      label={
                        <span>
                          Send invite to access {brandConfig.name}:{' '}
                          <InfoIcon
                            // filled={false}
                            className="tooltip-icon"
                            message={STRINGS.tooltip.create_team_member}
                          />
                        </span>
                      }
                      onChange={(checked: boolean) => setFieldValue('create_team_member', checked)}
                      isLock={this.isLockCreateTeamMember(values.email)}
                    />
                  </FormRow>
                )}

                {this.props.user.multiple_sn && (
                  <FormRow>
                    <Form.Item
                      labelCol={{span: 12}}
                      wrapperCol={{span: 12}}
                      label={
                        <span>
                          <span>Sales Navigator Subscription:</span>{' '}
                          <Tooltip title={STRINGS.info_user_sales_navigator}>
                            <InfoCircleOutlined type="info-circle" className="tooltip-icon" />
                          </Tooltip>
                        </span>
                      }
                    >
                      <div className="userEditModalFormRow">
                        <Select
                          className="userEditModalFormRowWithSelect"
                          placeholder="Subscription"
                          defaultValue={1}
                          onChange={(value: any) => setFieldValue('sn_account_selection', value)}
                          value={values.sn_account_selection}
                          getPopupContainer={trigger => trigger.parentElement}
                        >
                          {snSubsctiptionOptions.map(subsciption => (
                            <Select.Option key={`${subsciption.value}`} value={subsciption.value}>
                              {`${subsciption.label} Subscription`}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </Form.Item>
                  </FormRow>
                )}
                {this.props.error && (
                  <Row className="userEditModalFormRowWithMarginBottom">
                    <Alert message={this.props.error} type="error" />
                  </Row>
                )}

                {isAdmin && (
                  <>
                    <Divider className="userEditModalDivider"></Divider>
                    <FormRow>
                      <Form.Item
                        label={
                          <span>
                            Cookies: <AdminFeatureIcon />
                          </span>
                        }
                      >
                        <Button
                          disabled={!this.props.user.id || !values.cookies}
                          onClick={() => {
                            this.props.clearCookies(this.props.user.id);
                            setFieldValue('cookies', null);
                          }}
                        >
                          Clear cookies
                        </Button>
                      </Form.Item>
                      <Form.Item
                        label={
                          <span>
                            Disable/Enable user: <AdminFeatureIcon />
                          </span>
                        }
                      >
                        <Button
                          onClick={() => {
                            const newState = !values.disabled;
                            this.props.changeUserState(this.props.user.id, newState);
                            setFieldValue('disabled', newState);
                          }}
                        >
                          {values.disabled ? 'Enable' : 'Disable'}
                        </Button>
                      </Form.Item>
                      <Form.Item
                        label={
                          <span>
                            Last login error: <AdminFeatureIcon />
                          </span>
                        }
                      >
                        <span style={{marginRight: 5}}>
                          {values.last_login_error_date
                            ? moment(values.last_login_error_date).format('MM/DD/YYYY HH:mm')
                            : 'None'}
                        </span>
                        <Button
                          disabled={!this.props.user.id || !values.last_login_error_date}
                          onClick={() => {
                            this.props.resetLoginErrorDate(this.props.user.id);
                            setFieldValue('last_login_error_date', null);
                          }}
                        >
                          Reset
                        </Button>
                      </Form.Item>
                    </FormRow>
                    <FormRow>
                      Snooze login error pulse for this user for{' '}
                      <Select
                        style={{width: 60, marginRight: 5}}
                        value={this.state.login_error_snooze_days}
                        onChange={value => this.setState({login_error_snooze_days: value})}
                        getPopupContainer={trigger => trigger.parentElement}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(day => (
                          <Select.Option key={day} value={day}>
                            {day}
                          </Select.Option>
                        ))}
                      </Select>
                      day{this.state.login_error_snooze_days > 1 && 's'}
                      <Button type="primary" style={{marginLeft: 5, marginRight: 5}} onClick={this.snoozeLoginError}>
                        Snooze
                      </Button>
                      {<AdminFeatureIcon />}
                    </FormRow>
                    <div className="userEditModalFormRowWithMargin">
                      <div>
                        <Form.Item
                          label={
                            <span className="userEditModalFormRowWithBold">
                              Move user to other client as: <AdminFeatureIcon />
                            </span>
                          }
                        >
                          <Tooltip
                            className="userEditModalFormRowWithTooltip"
                            title="Please stop campaigns for executing this action"
                          >
                            <Select
                              disabled={this.props.client.enabled}
                              showSearch
                              placeholder="Select a client"
                              value={this.state.selectedClient}
                              onChange={selectedClient => this.setState({selectedClient})}
                              getPopupContainer={trigger => trigger.parentElement}
                            >
                              {affiliatedClients?.map(c => (
                                <Select.Option key={c?.id} value={c?.id}>{`${c?.email} (${c?.id})`}</Select.Option>
                              ))}
                            </Select>
                          </Tooltip>
                        </Form.Item>
                      </div>
                      <div className="userEditModalFormRowWithFlexEnd">
                        <Form.Item className="userEditModalFormRowWithSwitch">
                          <Switch
                            disabled={this.props.client.enabled}
                            onChange={moveUserWithCampaigns => this.setState({moveUserWithCampaigns})}
                            checked={this.state.moveUserWithCampaigns}
                          />
                        </Form.Item>
                        <div className="userEditModalFormRowWithText">Move with campaigns</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div>
                {isFirstUserForClient && (
                  <Alert
                    message={
                      <span>
                        {`Please read `}
                        <b>
                          {isMobile ? (
                            <UserRequirementsPopover>
                              <Button type="link" className="userRequirementsButtonLink">
                                LinkedIn User Recommendations
                              </Button>
                            </UserRequirementsPopover>
                          ) : (
                            <UserRequirementsTooltip>
                              <Button type="link" className="userRequirementsButtonLink">
                                LinkedIn User Recommendations
                              </Button>
                            </UserRequirementsTooltip>
                          )}
                        </b>
                      </span>
                    }
                    type="info"
                    className="userEditModalFormRowWithAlert"
                  />
                )}
                <div className="userEditModalButtons">
                  <div>
                    <TermsAndConditions />
                  </div>
                  <div>
                    <Button onClick={this.onClose}>Cancel</Button>
                    <Button
                      className="userEditModalSubmitButton"
                      loading={this.props.loading}
                      disabled={!!this.props.user.archived}
                      type="primary"
                      htmlType="submit"
                    >
                      {this.getSubmitTextButton(values.password)}
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
        <ConfirmationModal
          visible={this.state.confirmationVisible}
          onOk={this.closeModal}
          onCancel={() => this.setState({confirmationVisible: false})}
        />
      </BaseModal>
    );
  }
}
